import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { LoadingSpinner, UserCellProps } from '../../components/Molecules';
import { WebStoreReferralRewardTier0Template } from '../../components/Templates';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { ReferralSystem, RewardReferralRole } from '../../models/RewardStore';
import { Profile } from '../../models/UserStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

type State = {
  loadingTotal: number;
  finishRewardReferral: boolean;
  finishDistributorRewardReferral: boolean;
};

const { fetchRewardReferral, fetchRewardReferralUser } =
  useRewardStore.getState();

const WebStoreReferralRewardTier0Screen = () => {
  const navigate = useNavigate();
  const { distributorId } = useParams();

  const userId = useSessionStore((store) => store.activeUserId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));

  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? 'USD';
  }, [userProfile]);

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    finishRewardReferral: false,
    finishDistributorRewardReferral: false,
  });

  useRedirectEntry();

  useRedirectHome(!distributorId);

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRewardReferral({ referralSystem: ReferralSystem.Joyajoy })
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            finishRewardReferral: true,
          }));
        });
    }
  }, [userId]);

  const userRewardReferral = useRewardStore(
    (store) => store.getRewardReferral(`${ReferralSystem.Joyajoy}_${userId}`),
    shallow,
  );

  useRedirectHome(!userRewardReferral && state.finishRewardReferral);

  useEffect(() => {
    if (distributorId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRewardReferralUser(ReferralSystem.Joyajoy, distributorId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            finishDistributorRewardReferral: true,
          }));
        });
    }
  }, [distributorId, userId]);

  const distributorRewardReferral = useRewardStore(
    (store) =>
      store.getRewardReferral(`${ReferralSystem.Joyajoy}_${distributorId}`),
    shallow,
  );

  const distributorUserProfile = useUserStore((store) =>
    store.getProfile(distributorId ?? ''),
  );

  useRedirectHome(
    !distributorRewardReferral && state.finishDistributorRewardReferral,
  );

  const rewardReferralFieldName = `${distributorId}_all`;
  const rewardReferralField = useRewardStoreListField(
    'referrals',
    rewardReferralFieldName,
  );

  const fetchRewardReferrals = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore.getState().fetchRewardReferrals(
        {
          referralSystem: ReferralSystem.Joyajoy,
          fieldName: rewardReferralFieldName,
          referredBy: distributorId,
        },
        cursor,
      ),
    [distributorId, rewardReferralFieldName],
  );

  const {
    fetchMore: fetchMoreRewardReferrals,
    refresh: refreshRewardReferrals,
    hasCursor,
  } = useFetchList(rewardReferralField, fetchRewardReferrals);

  useEffect(() => {
    if (userId && distributorId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      refreshRewardReferrals()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
          }));
        });
    }
  }, [distributorId, refreshRewardReferrals, userId]);

  const loadMoreItems = useCallback(() => {
    fetchMoreRewardReferrals().catch(console.error);
  }, [fetchMoreRewardReferrals]);

  const rewardReferrals = useRewardStore((store) => {
    return store.getRewardReferrals(rewardReferralFieldName);
  }, arrayEqual(shallow));

  const onClickReferral = useCallback(
    (profile: Profile, userRole: RewardReferralRole) => {
      if (userRole === RewardReferralRole.Partner) {
        window.scrollTo(0, 0);
        navigate(
          `/store/referral/distributor/${distributorId}/${profile.userId}`,
        );
      } else {
        window.scrollTo(0, 0);
        navigate(`/store/referral/customer/${profile.userId}`);
      }
    },
    [distributorId, navigate],
  );
  const convertReferralsToUserCellData = useCallback(
    (referralRole: RewardReferralRole) => {
      return (
        rewardReferrals
          .filter((referral) => referral.role === referralRole)
          .map((referral) => {
            const referralProfile = useUserStore
              .getState()
              .getProfile(referral?.userId ?? '');
            const purchasedAt =
              referralRole === RewardReferralRole.Customer
                ? undefined
                : referral.referredAt;
            const confirmManageReward =
              userRewardReferral?.role === RewardReferralRole.Partner &&
              referralRole === RewardReferralRole.Distributor
                ? referral.managementReward?.confirmed.convertToCurrency(
                    currency,
                  )
                : undefined;
            const referralCount =
              userRewardReferral?.role === RewardReferralRole.Partner &&
              referralRole === RewardReferralRole.Distributor
                ? referralProfile?.statistics.joyajoyCustomerReferralCount
                : referralRole === RewardReferralRole.Customer
                ? referralProfile?.totalJoyajoyReferralCount
                : undefined;
            if (referralProfile && userRewardReferral) {
              const confirmReward = referral
                .getConfirmReward(userRewardReferral?.role)
                ?.convertToCurrency(currency);
              return {
                profile: referralProfile,
                purchasedAt: purchasedAt,
                confirmReward: confirmReward
                  ? `${currency}${confirmReward}`
                  : undefined,
                confirmManageReward: confirmManageReward
                  ? `${currency}${confirmManageReward}`
                  : undefined,
                referralCount: referralCount,
                hasCreditIcon:
                  userRewardReferral?.role === RewardReferralRole.Customer,
                onClickReferral: () => {
                  onClickReferral(referralProfile, userRewardReferral.role);
                },
              };
            } else {
              return null;
            }
          }) ?? []
      ).filter(Boolean) as UserCellProps[];
    },
    [currency, onClickReferral, rewardReferrals, userRewardReferral],
  );

  const data = useMemo<UserCellProps[]>(() => {
    return convertReferralsToUserCellData(RewardReferralRole.Customer);
  }, [convertReferralsToUserCellData]);

  return (
    <>
      {state.loadingTotal ? (
        <LoadingSpinner loading={!!state.loadingTotal} />
      ) : null}
      <WebStoreReferralRewardTier0Template
        profile={distributorUserProfile}
        data={data}
        hasCursor={hasCursor}
        loadMoreItems={loadMoreItems}
      />
    </>
  );
};

export default WebStoreReferralRewardTier0Screen;
